import { Injectable } from '@rxdi/core';

export interface MetaConfig {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

export interface FaqQuestion {
  question: string;
  answer: string;
}

@Injectable({ init: true })
export class SeoService {
  private activeJsonLdIds: string[] = [];

  updateMeta(config: MetaConfig): void {
    if (config.title) document.title = config.title;
    if (config.description) this.setMetaTag('description', config.description);
    if (config.ogTitle) this.setMetaProperty('og:title', config.ogTitle);
    if (config.ogDescription) this.setMetaProperty('og:description', config.ogDescription);
    if (config.ogType) this.setMetaProperty('og:type', config.ogType);
    if (config.ogUrl) this.setMetaProperty('og:url', config.ogUrl);
    if (config.ogImage) this.setMetaProperty('og:image', config.ogImage);
    if (config.twitterCard) this.setMetaProperty('twitter:card', config.twitterCard);
    if (config.twitterTitle) this.setMetaProperty('twitter:title', config.twitterTitle);
    if (config.twitterDescription) this.setMetaProperty('twitter:description', config.twitterDescription);
    if (config.twitterImage) this.setMetaProperty('twitter:image', config.twitterImage);
  }

  updateJsonLd(schema: object, id: string): void {
    this.clearJsonLd(id);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `jsonld-${id}`;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      ...schema,
    });
    document.head.appendChild(script);
    this.activeJsonLdIds.push(`jsonld-${id}`);
  }

  clearJsonLd(id: string): void {
    const script = document.getElementById(`jsonld-${id}`);
    if (script) {
      script.remove();
      this.activeJsonLdIds = this.activeJsonLdIds.filter((sid) => sid !== `jsonld-${id}`);
    }
  }

  clearAllJsonLd(): void {
    this.activeJsonLdIds.forEach((id) => {
      const script = document.getElementById(id);
      if (script) script.remove();
    });
    this.activeJsonLdIds = [];
  }

  updateOrganizationSchema(config: {
    name: string;
    url: string;
    logo?: string;
    email?: string;
    phone?: string;
    address?: string;
  }): void {
    this.updateJsonLd(
      {
        '@type': 'Organization',
        name: config.name,
        url: config.url,
        ...(config.logo && { logo: config.logo }),
        ...(config.email && {
          email: config.email,
          contactPoint: {
            '@type': 'ContactPoint',
            email: config.email,
            contactType: 'sales',
            ...(config.phone && { telephone: config.phone }),
          },
        }),
        ...(config.phone &&
          !config.email && {
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: config.phone,
              contactType: 'sales',
            },
          }),
      },
      'organization'
    );
  }

  updateFaqSchema(questions: FaqQuestion[]): void {
    this.updateJsonLd(
      {
        '@type': 'FAQPage',
        mainEntity: questions.map((q) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      },
      'faq'
    );
  }

  private setMetaTag(name: string, content: string): void {
    let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }

  private setMetaProperty(property: string, content: string): void {
    let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('property', property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }
}
